import { useState, useEffect } from "react";
import { supabase } from "../../supabaseClient";
import {
  Upload, MapPin, Globe, Instagram,
  Trophy, AlignLeft, Shield, Check,
  Image as ImageIcon, Loader2, Users,
  ChevronRight, Search, AlertCircle, Info
} from "lucide-react";
import { useNavigate } from "react-router-dom";

// --- CONSTANTS ---
const TEAM_RANKING = {
  "Varsity": 1, "Junior Varsity": 2, "JVC": 3, "Freshman": 4,
  "8th Grade": 5, "7th Grade": 6, "6th Grade": 7,
  "A Team": 8, "B Team": 9
};

const AVAILABLE_TEAMS = [
  "Varsity", "Junior Varsity", "JVC", "Freshman",
  "8th Grade", "7th Grade", "6th Grade",
  "A Team", "B Team"
];

const AGE_OPTIONS = [7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const GRADE_OPTIONS = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// --- REUSABLE COMPONENTS ---
const StepWizard = ({ currentStep, totalSteps, titles }) => {
  const progress = ((currentStep - 1) / (totalSteps - 1)) * 100;
  return (
    <div className="mb-8">
      <div className="flex justify-between mb-2">
        {titles.map((title, index) => (
          <span
            key={index}
            className={`text-xs font-bold uppercase tracking-wider ${index + 1 <= currentStep ? "text-blue-600" : "text-gray-300"}`}
          >
            {title}
          </span>
        ))}
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-600 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

const InputCard = ({ icon: Icon, label, value, onChange, placeholder, type = "text", error }) => (
  <div>
    <div className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${error ? 'border-red-300 ring-2 ring-red-50' : 'border-gray-100'}`}>
      <div className={`p-2 rounded-lg ${error ? 'bg-red-50 text-red-400' : 'bg-gray-50 text-gray-400'}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">{label}</label>
        <input
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 placeholder-gray-300"
        />
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

const SelectCard = ({ icon: Icon, label, value, onChange, options, error }) => (
  <div>
    <div className={`bg-white rounded-xl p-3 flex items-center gap-3 border shadow-sm transition-all focus-within:ring-2 focus-within:ring-blue-100 ${error ? 'border-red-300 ring-2 ring-red-50' : 'border-gray-100'}`}>
      <div className={`p-2 rounded-lg ${error ? 'bg-red-50 text-red-400' : 'bg-gray-50 text-gray-400'}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 relative">
        <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-0.5">{label}</label>
        <select
          value={value}
          onChange={onChange}
          className="w-full text-gray-900 font-bold text-sm bg-transparent border-none p-0 focus:ring-0 appearance-none cursor-pointer"
        >
          <option value="">Select...</option>
          {options.map((opt) => (
            <option key={opt.name || opt} value={opt.name || opt}>{opt.name || opt}</option>
          ))}
        </select>
      </div>
    </div>
    {error && <p className="text-xs text-red-500 mt-1 pl-1 font-medium">{error}</p>}
  </div>
);

const AgeRangeSelector = ({ label, minVal, maxVal, onMinChange, onMaxChange, options, minPlaceholder, maxPlaceholder }) => (
  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">{label}</p>
    <div className="flex items-center gap-2">
      <select
        value={minVal || ""}
        onChange={(e) => onMinChange(e.target.value ? Number(e.target.value) : "")}
        className="flex-1 bg-white border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">{minPlaceholder}</option>
        {options.map(level => <option key={level} value={level}>{level}</option>)}
      </select>
      <span className="text-gray-400 font-bold">-</span>
      <select
        value={maxVal || ""}
        onChange={(e) => onMaxChange(e.target.value ? Number(e.target.value) : "")}
        className="flex-1 bg-white border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">{maxPlaceholder}</option>
        {options.map(level => (
          <option key={level} value={level} disabled={minVal && level < minVal}>{level}</option>
        ))}
      </select>
    </div>
  </div>
);

// --- MAIN PAGE ---
export default function CreateProgram() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [user, setUser] = useState(null);
  const [sports, setSports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "",
    description: "",
    sport: "",
    program_type: "",
    teams_offered: [],
    boys_min: "", boys_max: "",
    girls_min: "", girls_max: "",
    registration_format: "age",
    in_season_start_month: 11,
    in_season_end_month: 3,
    off_season_start_month: 4,
    off_season_end_month: 10,
    website_url: "",
    ig_handle: "",
    location_address: "",
    latitude: null,
    longitude: null,
    program_logo: null,
    sponsorships: [],
  });

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUser(user);
      const { data } = await supabase.from("development_sports").select("name");
      if (data) setSports(data);
    };
    init();
  }, []);

  const handleTypeChange = (type) => {
    let newTeams = [];
    if (type === "High School Program") newTeams = ["Varsity", "Junior Varsity", "Freshman"];
    else if (type === "Middle School Program") newTeams = ["8th Grade", "7th Grade", "6th Grade"];

    setForm(prev => ({
      ...prev,
      program_type: type,
      teams_offered: newTeams
    }));
    setErrors(prev => ({ ...prev, program_type: null }));
  };

  const handleAddressSearch = async () => {
    if (!form.location_address) return;
    setAddressLoading(true);
    setErrors(prev => ({ ...prev, location_address: null }));

    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(form.location_address)}`
      );
      const data = await response.json();

      if (data && data.length > 0) {
        const result = data[0];
        const address = result.address;
        const hasSpecificAddress = address.road || address.house_number || address.building || address.industrial || address.amenity;

        if (!hasSpecificAddress) {
          setErrors(prev => ({ ...prev, location_address: "Please provide a specific street address." }));
          setForm(prev => ({ ...prev, latitude: null, longitude: null }));
          return;
        }

        setForm(prev => ({
          ...prev,
          location_address: result.display_name,
          latitude: parseFloat(result.lat),
          longitude: parseFloat(result.lon),
        }));
      } else {
        setErrors(prev => ({ ...prev, location_address: "Address not found." }));
      }
    } catch (err) {
      setErrors(prev => ({ ...prev, location_address: "Search service error." }));
    } finally {
      setAddressLoading(false);
    }
  };

  const handleUpload = async (e, bucket, field, isMultiple = false) => {
    try {
      setUploading(true);
      const files = Array.from(e.target.files);
      if (!files.length) return;

      const newUrls = [];
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${fileExt}`;
        const { error: uploadError } = await supabase.storage.from(bucket).upload(fileName, file);
        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage.from(bucket).getPublicUrl(fileName);
        newUrls.push(publicUrl);
      }

      setForm(prev => ({
        ...prev,
        [field]: isMultiple ? [...prev[field], ...newUrls] : newUrls[0]
      }));
    } catch (error) {
      setErrors(prev => ({ ...prev, global: "Upload failed." }));
    } finally {
      setUploading(false);
    }
  };

  const validateStep = (currentStep) => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!form.name.trim()) newErrors.name = "Program Name is required";
      if (!form.sport) newErrors.sport = "Sport is required";
    }
    if (currentStep === 2) {
      if (!form.program_type) newErrors.program_type = "Please select a classification";
    }
    if (currentStep === 3) {
      if (form.teams_offered.length === 0) newErrors.teams_offered = "Select at least one team";
      const hasBoys = form.boys_min && form.boys_max;
      const hasGirls = form.girls_min && form.girls_max;
      if (!hasBoys && !hasGirls) {
        newErrors.ages = `Set at least one complete ${form.registration_format === "grade" ? "grade" : "age"} range`;
      }
      if (!form.in_season_start_month || !form.in_season_end_month) {
        newErrors.in_season = "Set in-season start and end months";
      }
      if (!form.off_season_start_month || !form.off_season_end_month) {
        newErrors.off_season = "Set off-season start and end months";
      }
    }
    if (currentStep === 4) {
      if (!form.location_address) newErrors.location_address = "Address is required";
      if (!form.latitude) newErrors.location_address = "Search and verify a specific address";
      if (!form.website_url?.trim()) newErrors.website_url = "Website is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;
    setUploading(true);
    try {
      const generateAgeArray = (min, max) => (min && max) ? Array.from({ length: (max - min) + 1 }, (_, i) => String(min + i)) : [];
      const boysAges = generateAgeArray(form.boys_min, form.boys_max);
      const girlsAges = generateAgeArray(form.girls_min, form.girls_max);
      const allAges = [...new Set([...boysAges, ...girlsAges])].sort((a, b) => a - b);
      const locationPoint = `POINT(${form.longitude} ${form.latitude})`;

      const { data: newProgram, error: progError } = await supabase
        .from("programs")
        .insert([{
          name: form.name,
          description: form.description,
          sport: form.sport,
          program_type: form.program_type,
          registration_format: form.registration_format,
          in_season_start_month: Number(form.in_season_start_month),
          in_season_end_month: Number(form.in_season_end_month),
          off_season_start_month: Number(form.off_season_start_month),
          off_season_end_month: Number(form.off_season_end_month),
          age_groups: allAges,
          boys_ages_offered: boysAges,
          girls_ages_offered: girlsAges,
          website_url: form.website_url,
          ig_handle: form.ig_handle,
          location_address: form.location_address,
          location: locationPoint,
          latitude: form.latitude,
          longitude: form.longitude,
          administrator: user.id,
          created_by: user.id,
          program_logo: form.program_logo,
          sponsorships: form.sponsorships
        }])
        .select().single();

      if (progError) throw progError;

      const teamsToInsert = form.teams_offered.map((teamName) => ({
        name: teamName, program_id: newProgram.id, sport: form.sport, location_address: form.location_address,
      }));

      const { data: createdTeams, error: teamError } = await supabase.from("teams").insert(teamsToInsert).select();
      if (teamError) throw teamError;

      const sortedTeams = createdTeams.sort((a, b) => (TEAM_RANKING[a.name] || 99) - (TEAM_RANKING[b.name] || 99));
      navigate(`/admin/program/${newProgram.id}/team/${sortedTeams[0].id}/dashboard`);
    } catch (error) {
      setErrors({ global: error.message });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
      {/* HEADER */}
      <div className="bg-white px-6 py-4 shadow-sm border-b border-gray-100 flex justify-center sticky top-0 z-50">
        <h1 className="text-lg font-extrabold text-gray-900 tracking-tight text-center">Create Organization</h1>
      </div>

      <div className="flex-1 max-w-2xl w-full mx-auto p-6">
        <StepWizard
          currentStep={step}
          totalSteps={4}
          titles={["Identity", "Type", "Structure", "Branding"]}
        />

        {errors.global && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle size={18} />
            <span className="text-sm font-bold">{errors.global}</span>
          </div>
        )}

        {/* STEP 1: IDENTITY */}
        {step === 1 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Identity</h2>
              <div className="grid gap-4">
                <InputCard icon={Shield} label="Program Name" placeholder="e.g. Shoreline Academy" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} error={errors.name} />
                <SelectCard icon={Trophy} label="Sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} options={sports} error={errors.sport} />
                <div className="bg-white rounded-xl p-3 border border-gray-100 flex gap-3">
                  <div className="bg-gray-50 p-2 rounded-lg text-gray-400 h-fit"><AlignLeft size={18} /></div>
                  <div className="flex-1">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Description</label>
                    <textarea rows={4} placeholder="Tell us about your mission..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full text-gray-900 font-medium text-sm border-none p-0 focus:ring-0 resize-none" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: CLASSIFICATION */}
        {step === 2 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Program Classification</h2>
              <p className="text-sm text-gray-500 mb-6">Select the type that best describes your organization.</p>
              <div className="grid grid-cols-1 gap-3">
                {["Club/AAU", "High School Program", "Middle School Program", "College Program", "Little League"].map((type) => (
                  <label key={type} className={`relative rounded-2xl p-5 border-2 cursor-pointer transition-all flex items-center justify-between ${form.program_type === type ? "bg-blue-50 border-blue-500" : "bg-white border-gray-100"}`}>
                    <span className={`text-base font-bold ${form.program_type === type ? "text-blue-700" : "text-gray-700"}`}>{type}</span>
                    {form.program_type === type && <Check size={18} className="text-blue-600" />}
                    <input type="radio" className="hidden" name="type" checked={form.program_type === type} onChange={() => handleTypeChange(type)} />
                  </label>
                ))}
              </div>
              {errors.program_type && <p className="text-xs text-red-500 mt-4 font-bold">{errors.program_type}</p>}
            </div>
          </div>
        )}

        {/* STEP 3: STRUCTURE */}
        {step === 3 && (
          <div className="space-y-6 animate-fadeIn">
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <div className="flex items-center gap-2 mb-4"><Users className="text-blue-500" size={20} /><h2 className="text-lg font-bold text-gray-900">Teams Offered</h2></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {AVAILABLE_TEAMS.map((team) => (
                  <button key={team} onClick={() => setForm({ ...form, teams_offered: form.teams_offered.includes(team) ? form.teams_offered.filter(t => t !== team) : [...form.teams_offered, team] })} className={`px-3 py-3 rounded-xl text-sm font-bold border-2 transition-all flex justify-between items-center ${form.teams_offered.includes(team) ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-gray-50 border-transparent text-gray-500 hover:bg-gray-100"}`}>{team}{form.teams_offered.includes(team) && <Check size={14} />}</button>
                ))}
              </div>
              {errors.teams_offered && <p className="text-xs text-red-500 mt-2 font-bold">{errors.teams_offered}</p>}
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-2">Registration Format</h2>
              <p className="text-sm text-gray-500 mb-4">Choose whether athletes register by age or grade.</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "age", label: "Age (8-18U)" },
                  { value: "grade", label: "Grade (3rd-12th)" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setForm(prev => ({ ...prev, registration_format: option.value, boys_min: "", boys_max: "", girls_min: "", girls_max: "" }))}
                    className={`rounded-xl border-2 p-4 text-sm font-bold transition-all ${form.registration_format === option.value ? "bg-blue-50 border-blue-500 text-blue-700" : "bg-gray-50 border-gray-200 text-gray-600"}`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">
                {form.registration_format === "grade" ? "Grade Demographics" : "Age Demographics"}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <AgeRangeSelector
                  label={form.registration_format === "grade" ? "Boys Grades" : "Boys Ages"}
                  minVal={form.boys_min}
                  maxVal={form.boys_max}
                  onMinChange={(v) => setForm({ ...form, boys_min: v })}
                  onMaxChange={(v) => setForm({ ...form, boys_max: v })}
                  options={form.registration_format === "grade" ? GRADE_OPTIONS : AGE_OPTIONS}
                  minPlaceholder={form.registration_format === "grade" ? "Min Grade" : "Min Age"}
                  maxPlaceholder={form.registration_format === "grade" ? "Max Grade" : "Max Age"}
                />
                <AgeRangeSelector
                  label={form.registration_format === "grade" ? "Girls Grades" : "Girls Ages"}
                  minVal={form.girls_min}
                  maxVal={form.girls_max}
                  onMinChange={(v) => setForm({ ...form, girls_min: v })}
                  onMaxChange={(v) => setForm({ ...form, girls_max: v })}
                  options={form.registration_format === "grade" ? GRADE_OPTIONS : AGE_OPTIONS}
                  minPlaceholder={form.registration_format === "grade" ? "Min Grade" : "Min Age"}
                  maxPlaceholder={form.registration_format === "grade" ? "Max Grade" : "Max Age"}
                />
              </div>
              {errors.ages && <p className="text-xs text-red-500 mt-2 font-bold">{errors.ages}</p>}
            </div>
            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Season Windows</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className={`rounded-xl border p-4 ${errors.in_season ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">In-Season</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={form.in_season_start_month || ""}
                      onChange={(e) => setForm({ ...form, in_season_start_month: Number(e.target.value) })}
                      className="flex-1 bg-white border border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {MONTH_OPTIONS.map((month) => (
                        <option key={`in-start-${month.value}`} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                    <span className="text-gray-400 font-bold">-</span>
                    <select
                      value={form.in_season_end_month || ""}
                      onChange={(e) => setForm({ ...form, in_season_end_month: Number(e.target.value) })}
                      className="flex-1 bg-white border border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {MONTH_OPTIONS.map((month) => (
                        <option key={`in-end-${month.value}`} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>
                  {errors.in_season && <p className="text-xs text-red-500 mt-2 font-bold">{errors.in_season}</p>}
                </div>

                <div className={`rounded-xl border p-4 ${errors.off_season ? "border-red-300 bg-red-50" : "border-gray-200 bg-gray-50"}`}>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Off-Season</p>
                  <div className="flex items-center gap-2">
                    <select
                      value={form.off_season_start_month || ""}
                      onChange={(e) => setForm({ ...form, off_season_start_month: Number(e.target.value) })}
                      className="flex-1 bg-white border border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {MONTH_OPTIONS.map((month) => (
                        <option key={`off-start-${month.value}`} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                    <span className="text-gray-400 font-bold">-</span>
                    <select
                      value={form.off_season_end_month || ""}
                      onChange={(e) => setForm({ ...form, off_season_end_month: Number(e.target.value) })}
                      className="flex-1 bg-white border border-gray-300 rounded-lg text-sm font-bold py-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {MONTH_OPTIONS.map((month) => (
                        <option key={`off-end-${month.value}`} value={month.value}>{month.label}</option>
                      ))}
                    </select>
                  </div>
                  {errors.off_season && <p className="text-xs text-red-500 mt-2 font-bold">{errors.off_season}</p>}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* STEP 4: BRANDING */}
        {step === 4 && (
          <div className="space-y-6 animate-fadeIn">
             <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Location</h2>
              <div className="flex gap-2">
                <div className="flex-1"><InputCard icon={MapPin} label="Street Address" placeholder="123 Main St, City, State" value={form.location_address} onChange={(e) => setForm({ ...form, location_address: e.target.value, latitude: null, longitude: null })} error={errors.location_address} /></div>
                <button onClick={handleAddressSearch} disabled={addressLoading} className="h-[52px] w-12 bg-blue-600 text-white rounded-xl flex items-center justify-center">{addressLoading ? <Loader2 className="animate-spin" size={20}/> : <Search size={20}/>}</button>
              </div>
              {form.latitude && <div className="mt-3 flex items-center gap-2 text-emerald-600 bg-emerald-50 p-3 rounded-lg border border-emerald-100"><Check size={16} /><span className="text-xs font-bold uppercase">Verified</span></div>}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className={`bg-white p-6 rounded-3xl shadow-sm border flex flex-col items-center text-center relative group overflow-hidden ${errors.program_logo ? 'border-red-300 bg-red-50' : 'border-gray-100'}`}>
                <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "program_logos", "program_logo")} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                <div className="mb-3 p-3 bg-blue-50 rounded-full">{form.program_logo ? <img src={form.program_logo} className="w-16 h-16 rounded-full object-cover" alt="Logo"/> : <ImageIcon className="text-blue-500" size={32}/>}</div>
                <p className="text-sm font-bold text-gray-900">Upload Logo</p>
                {errors.program_logo && <p className="text-xs text-red-500 mt-1 font-bold">{errors.program_logo}</p>}
              </div>
              <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col items-center text-center relative group overflow-hidden">
                <input type="file" multiple accept="image/*" onChange={(e) => handleUpload(e, "program_assets", "sponsorships", true)} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                <div className="mb-3 p-3 bg-green-50 rounded-full"><Upload className="text-green-600" size={32} /></div>
                <p className="text-sm font-bold text-gray-900">Upload Sponsors</p>
              </div>
            </div>

            <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Presence</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <InputCard icon={Globe} label="Website" placeholder="www.site.com" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} error={errors.website_url} />
                <InputCard icon={Instagram} label="Instagram" placeholder="@handle" value={form.ig_handle} onChange={(e) => setForm({ ...form, ig_handle: e.target.value })} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* STICKY FOOTER NAVIGATION */}
      <div className="bg-white p-4 border-t border-gray-200 sticky bottom-0 z-50 flex justify-center mt-auto">
        <div className="max-w-2xl w-full flex gap-3">
          {step > 1 && (
            <button
              onClick={() => setStep(prev => prev - 1)}
              className="px-6 py-3 font-bold text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
            >
              Back
            </button>
          )}
          <button
            onClick={step === 4 ? handleSubmit : handleNext}
            disabled={uploading}
            className="flex-1 bg-blue-600 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-200 transition-all active:scale-[0.98] disabled:opacity-50"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="animate-spin" size={18} /> Saving...
              </span>
            ) : (
              step === 4 ? "Create Organization" : <span className="flex items-center justify-center gap-1">Next <ChevronRight size={18} /></span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
